import cx from 'classnames'
import React from 'react'

import { Fields, InjectedFormProps } from 'redux-form'

import Accordion from '@cinch/oxd-components/filter-accordion/src'
import FilterLozenge from '@cinch/oxd-components/filter-lozenge/src'
import Text, { TextStyles } from '@cinch/oxd-components/text/src'
import MoneyInput from '@cinch/oxd-components/your-money/src'
import { FiltersPropsFromDispatch } from '@cinch/oxd-containers/listing-filters/src'
import { currencyValueFormatter } from '@cinch/oxd-utils/form/src'

import { filtersConfig } from './listing-filters-config'
import { getAccordionFilter, getLozengeGroups } from './listing-filters.utils'

import * as styles from './listing-filters.module.css'

interface Option {
  label: string
  value: string
}

export interface ListingFiltersProps extends Partial<InjectedFormProps<any, any>>, FiltersPropsFromDispatch {
  i18n?: Pick<Cinch.WebAppTranslations['findVehicle'], 'filters' | 'deliveryPreference' | 'deliveryModal'>
  makes?: Option[]
  models?: Option[]
  selectedMake?: string
  moneyData?: Cinch.IMoneyFormData
  deliveryOption?: Cinch.FilterFormData['deliveryOption']
  distance?: number
  change(field: string, value: any): void
  reset(): void
}

const ListingFilters: React.FC<ListingFiltersProps> = ({
  i18n = {
    filters: {
      title: 'Filter vehicles',
      settings: 'Settings',
      filters: 'Filters',
      clear: 'Clear All',
      location: 'Location',
      money: {
        title: 'Money',
        cash: 'Cash',
        partEx: 'Part-ex',
        finance: 'Finance',
        submit: 'Save',
        total: 'Total',
      },
      within: 'Within',
      of: 'of',
    },
    deliveryPreference: {
      title: 'Delivered cars',
      moreInfo: 'More info',
      bullet1: '7-day money back guarantee',
      bullet2: 'Thorough 150+ point inspection',
      onLabel: 'on',
      offLabel: 'off',
    },
    deliveryModal: {
      bullet1: 'Buy online. Free home delivery',
      bullet2: '7-day money back guarantee',
      bullet3: 'Thorough 150+ point inspection',
      bullet4: '90-day warranty',
      bullet5: 'Buy, finance and part exchange entirely online',
      bullet6: 'No hidden or extra admin fees',
    },
  },
  models = [],
  makes = [],
  selectedMake,
  moneyData,
  updateUserProfile,
  resetMoney,
  change,
  clearFilters,
}) => {
  const { title, settings, filters, clear, money } = i18n.filters

  const expanded: any[] = []
  const [expandedAccordions, updateExpandedAccordions] = React.useState(expanded)

  const handleToggle = (id: string | number, isExpanded: boolean) => {
    if (isExpanded) {
      updateExpandedAccordions([...expandedAccordions, id])
    } else {
      updateExpandedAccordions(expandedAccordions.filter((accordionId) => accordionId !== id))
    }
  }

  const getAccordion = (config: any) => {
    const newConfig = config

    if (config.key === 'make') {
      config.options = makes
    }

    if (config.key === 'selectedModel') {
      config.options = models
    }

    const lozengeGroup = <div className={styles.lozengeGroup}>{getLozengeGroups(newConfig, models, makes)}</div>

    return (
      <Accordion
        key={config.label}
        id={config.label}
        title={config.label}
        lozengeGroup={lozengeGroup}
        toggle={handleToggle}
        isExpanded={expandedAccordions.includes(config.label)}
        isDisabled={config.key === 'selectedModel' && !selectedMake}
      >
        <div className={styles.filterContent}>{getAccordionFilter(newConfig, change, makes, models, selectedMake)}</div>
      </Accordion>
    )
  }

  const resetFilters = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent> | React.KeyboardEvent<HTMLButtonElement>,
  ) => {
    e.preventDefault()
    updateExpandedAccordions([])
    clearFilters()
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.filters}>
        <div className={styles.filtersHeading}>
          <Text type={TextStyles.menuTitle} element="h3">
            {settings}
          </Text>
        </div>

        <div className={styles.filtersBody}>
          <Accordion
            data-testid="money-accordion"
            title={money.title}
            lozengeGroup={
              !!moneyData &&
              !!moneyData.budget && (
                <div className={styles.lozenges}>
                  <FilterLozenge
                    onRemove={resetMoney}
                    label={!!moneyData && moneyData.budget ? currencyValueFormatter(moneyData.budget) : ''}
                    value={(!!moneyData && moneyData.budget) || ''}
                  />
                </div>
              )
            }
          >
            <div className={cx(styles.filterContent, styles.money)}>
              <MoneyInput
                i18n={money}
                onSubmit={updateUserProfile}
                initialValues={moneyData}
                buttonSize={'mini'}
                hideAdditionalOptions={true}
                compact={true}
              />
            </div>
          </Accordion>
        </div>
      </div>
      <form aria-label={title} className={styles.filters}>
        <div className={styles.filtersHeading}>
          <Text type={TextStyles.menuTitle} element="h3">
            {filters}
          </Text>
          <button
            test-id="clearButton"
            className={styles.clearButton}
            onClick={(e) => resetFilters(e)}
            onKeyDown={(e) => e.key === 'Enter' && resetFilters(e)}
            tabIndex={0}
            children={clear}
            type="button"
          />
        </div>

        <Fields
          names={['longitude', 'latitude', 'toPrice', 'sortingCriteria']}
          component={(values) => {
            if (!moneyData || !moneyData.budget || values.toPrice.input.value !== moneyData.budget) {
              const budget = (moneyData && moneyData.budget) || -1
              values.toPrice.input.onChange(budget)
            }

            return null
          }}
        />

        <div className={styles.filtersBody}>{filtersConfig && filtersConfig.map((filter) => getAccordion(filter))}</div>
      </form>
    </div>
  )
}

export default ListingFilters
